import { Button } from '@/components/shadcn/button'
import { TableCell, TableRow } from '@/components/shadcn/table'
import { Badge } from '@/components/shadcn/badge'
import { DynamicIcon } from '@/components/icons/DynamicIcon'
import {
  IconCirclePlusOutline,
  IconDeleteOutline,
  IconDownChevronCircleOutline,
  IconEditOutline,
  IconHelpQuestionOutline,
  IconRightChevronOutline,
  IconUpChevronCircleOutline,
} from '@/components/icons'
import { TooltipWrapper } from '@/components/ui/TooltipWrapper'
import { ComposedSheet } from '@/components/ui/ComposedSheet'
import { MenuItemForm } from './MenuItemForm'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { TooltipButton } from '@/components/ui/TooltipButton'
import clsx from 'clsx'
import { MessagesLandingMenuItem } from './types'

interface MenuItemProps {
  item: MessagesLandingMenuItem
  id: string
  depth: number
  isExpanded: boolean
  hasChildren: boolean
  showIcon: boolean
  toggleItem: (id: string) => void
  moveItemUp: (id: string) => void
  moveItemDown: (id: string) => void
  addChildItem: (id: string, newItem: MessagesLandingMenuItem) => void
  deleteItem: (id: string) => void
  editItem: (id: string, updatedItem: Partial<MessagesLandingMenuItem>) => void
}

export function MenuItem({
  item,
  id,
  depth,
  isExpanded,
  hasChildren,
  showIcon,
  toggleItem,
  moveItemUp,
  moveItemDown,
  addChildItem,
  deleteItem,
  editItem,
}: MenuItemProps) {
  const paddingLeft = depth * 20

  return (
    <TableRow>
      <TableCell className="w-10">
        {showIcon ? (
          item.icon_name ? (
            <DynamicIcon
              iconName={item.icon_name}
              className="h-5 w-5"
              fallback={<IconHelpQuestionOutline className="size-5" />}
            />
          ) : (
            <IconHelpQuestionOutline className="size-5" />
          )
        ) : null}
      </TableCell>
      <TableCell>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ paddingLeft: `${paddingLeft}px` }}>
            {hasChildren ? (
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => toggleItem(id)}>
                <IconRightChevronOutline className={clsx('size-3 transition-transform', { 'rotate-90': isExpanded })} />
              </Button>
            ) : (
              <div style={{ width: 24 }} />
            )}
            <span className={hasChildren ? 'font-medium' : ''}>{item.name}</span>
          </div>
        </div>
      </TableCell>
      <TableCell>{item.page_id}</TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-1">
          {item.roles.map((role, i) => (
            <Badge key={i} variant="outline" className="bg-background">
              {role}
            </Badge>
          ))}
        </div>
      </TableCell>
      <TableCell>
        <div className="flex w-full items-center justify-between">
          <div className="inline-flex items-center gap-x-2">
            <TooltipWrapper
              tooltip={`Edit menu item: "${item.name}"`}
              wrapTrigger
              triggerComponent={
                <ComposedSheet trigger={undefined} title={`Edit menu item: "${item.name}"`}>
                  <div className="px-1">
                    <MenuItemForm
                      closeSheetOnSubmit
                      onSubmit={(data) => {
                        const formData = data as Record<string, unknown>
                        const updatedItem: Partial<MessagesLandingMenuItem> = {
                          item_type: String(formData.item_type || ''),
                          name: String(formData.name || ''),
                          page_id: String(formData.page_id || ''),
                          description: String(formData.description || ''),
                          icon_name: formData.icon_name ? String(formData.icon_name) : undefined,
                          roles: (formData.assignedRoles as string[]) ?? item.roles,
                        }
                        editItem(id, updatedItem)
                      }}
                      initialValues={item}
                      showIcon={showIcon}
                    />
                  </div>
                </ComposedSheet>
              }
            >
              <Button variant="ghost" size="icon" className="size-6">
                <IconEditOutline size={14} />
              </Button>
            </TooltipWrapper>
            <TooltipWrapper
              tooltip="Add child item"
              wrapTrigger
              triggerComponent={
                <ComposedSheet trigger={undefined}>
                  <div className="px-1">
                    <MenuItemForm
                      closeSheetOnSubmit
                      showIcon={false}
                      onSubmit={(data) => {
                        const formData = data as Record<string, unknown>
                        const menuItem: MessagesLandingMenuItem = {
                          item_type: String(formData.item_type || ''),
                          name: String(formData.name || ''),
                          page_id: String(formData.page_id || ''),
                          description: String(formData.description || ''),
                          icon_name: formData.icon_name ? String(formData.icon_name) : undefined,
                          roles: (formData.assignedRoles as string[]) ?? [],
                          items: [],
                        }
                        addChildItem(id, menuItem)
                      }}
                    />
                  </div>
                </ComposedSheet>
              }
            >
              <Button variant="ghost" size="icon" className="size-6">
                <IconCirclePlusOutline size={14} />
              </Button>
            </TooltipWrapper>
            <TooltipWrapper
              tooltip="Delete Menu Item"
              wrapTrigger
              triggerComponent={<ConfirmDialog title="Delete Menu Item" onConfirm={() => deleteItem(id)} />}
            >
              <Button variant="ghost" size="icon" className="size-6">
                <IconDeleteOutline size={14} />
              </Button>
            </TooltipWrapper>
          </div>
          <div className="inline-flex items-center gap-x-2">
            <TooltipButton tooltip="Move up">
              <Button variant="ghost" size="icon" className="size-6" onClick={() => moveItemUp(id)}>
                <IconUpChevronCircleOutline size={20} />
              </Button>
            </TooltipButton>
            <TooltipButton tooltip="Move down">
              <Button variant="ghost" size="icon" className="size-6" onClick={() => moveItemDown(id)}>
                <IconDownChevronCircleOutline size={20} />
              </Button>
            </TooltipButton>
          </div>
        </div>
      </TableCell>
    </TableRow>
  )
}
